'use client';

import { useRef, useCallback, useState, useEffect, useLayoutEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bold,
  Italic,
  Heading1,
  Heading2,
  Heading3,
  Quote,
  List,
  ListOrdered,
} from 'lucide-react';

import { usePageBuilderContext } from '../page-builder-context';
import type { TextBlock, TextVariant } from '@/lib/types/page-builder';
import { cn } from '@/lib/utils';

// Debounce helper
function useDebouncedCallback<T extends (...args: Parameters<T>) => void>(
  callback: T,
  delay: number
): T {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  return useCallback(
    ((...args: Parameters<T>) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        callback(...args);
      }, delay);
    }) as T,
    [callback, delay]
  );
}

// ============================================================================
// TYPES
// ============================================================================

interface TextBlockEditorProps {
  block: TextBlock;
}

// ============================================================================
// TOOLBAR
// ============================================================================

interface ToolbarButtonProps {
  icon: typeof Bold;
  label: string;
  isActive?: boolean;
  onClick: () => void;
}

function ToolbarButton({ icon: Icon, label, isActive, onClick }: ToolbarButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-8 h-8 flex items-center justify-center rounded-md',
        'transition-colors duration-100',
        isActive
          ? 'bg-primary-subtle text-primary-text'
          : 'text-foreground-tertiary hover:text-foreground hover:bg-muted'
      )}
      aria-label={label}
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}

interface FormatToolbarProps {
  visible: boolean;
  onFormat: (command: string, value?: string) => void;
  onVariantChange: (variant: TextVariant) => void;
  currentVariant: TextVariant;
}

function FormatToolbar({
  visible,
  onFormat,
  onVariantChange,
  currentVariant,
}: FormatToolbarProps) {
  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.1 }}
          className={cn(
            'absolute -top-12 left-0 z-10',
            'flex items-center gap-0.5 p-1',
            'bg-card border border-border rounded-lg shadow-lg'
          )}
        >
          {/* Text variant buttons */}
          <ToolbarButton
            icon={Heading1}
            label="Заголовок 1"
            isActive={currentVariant === 'heading1'}
            onClick={() => onVariantChange('heading1')}
          />
          <ToolbarButton
            icon={Heading2}
            label="Заголовок 2"
            isActive={currentVariant === 'heading2'}
            onClick={() => onVariantChange('heading2')}
          />
          <ToolbarButton
            icon={Heading3}
            label="Заголовок 3"
            isActive={currentVariant === 'heading3'}
            onClick={() => onVariantChange('heading3')}
          />
          <ToolbarButton
            icon={Quote}
            label="Цитата"
            isActive={currentVariant === 'quote'}
            onClick={() => onVariantChange('quote')}
          />

          <div className="w-px h-5 bg-border mx-1" />

          {/* Formatting buttons */}
          <ToolbarButton
            icon={Bold}
            label="Жирный"
            onClick={() => onFormat('bold')}
          />
          <ToolbarButton
            icon={Italic}
            label="Курсив"
            onClick={() => onFormat('italic')}
          />

          <div className="w-px h-5 bg-border mx-1" />

          {/* List buttons */}
          <ToolbarButton
            icon={List}
            label="Маркированный список"
            onClick={() => onFormat('insertUnorderedList')}
          />
          <ToolbarButton
            icon={ListOrdered}
            label="Нумерованный список"
            onClick={() => onFormat('insertOrderedList')}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function TextBlockEditor({ block }: TextBlockEditorProps) {
  const { updateBlock, openInsertMenu, focusedBlockId } = usePageBuilderContext();
  const editorRef = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [showToolbar, setShowToolbar] = useState(false);
  const isInitializedRef = useRef(false);
  const lastContentRef = useRef(block.content.html);

  const isFocusedBlock = focusedBlockId === block.id;

  // Set initial content only once
  useLayoutEffect(() => {
    if (editorRef.current && !isInitializedRef.current) {
      editorRef.current.innerHTML = block.content.html;
      isInitializedRef.current = true;
    }
  }, []);

  // Sync external changes only when not focused (e.g., undo/redo)
  useEffect(() => {
    if (!isFocused && editorRef.current && block.content.html !== lastContentRef.current) {
      editorRef.current.innerHTML = block.content.html;
      lastContentRef.current = block.content.html;
    }
  }, [block.content.html, isFocused]);

  // Debounced update to parent
  const debouncedUpdate = useDebouncedCallback(
    (html: string, plainText: string) => {
      lastContentRef.current = html;
      updateBlock(block.id, {
        content: { html, plainText },
        updatedAt: Date.now(),
      });
    },
    300
  );

  // Update content - just sync to parent, don't re-render
  const handleInput = useCallback(() => {
    if (!editorRef.current) return;

    const html = editorRef.current.innerHTML;
    const plainText = editorRef.current.innerText;

    debouncedUpdate(html, plainText);
  }, [debouncedUpdate]);

  // Handle slash command
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const plainText = editorRef.current?.innerText || '';

      // Slash command at start of empty block
      if (e.key === '/' && !plainText.trim()) {
        e.preventDefault();
        openInsertMenu(block.id);
        return;
      }
    },
    [block.id, openInsertMenu]
  );

  // Format command
  const handleFormat = useCallback((command: string, value?: string) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleInput();
  }, [handleInput]);

  // Change text variant
  const handleVariantChange = useCallback(
    (variant: TextVariant) => {
      updateBlock(block.id, {
        style: { variant },
        updatedAt: Date.now(),
      });
    },
    [block.id, updateBlock]
  );

  // Show toolbar on selection
  useEffect(() => {
    const handleSelectionChange = () => {
      if (!isFocused) return;
      const selection = window.getSelection();
      setShowToolbar(!!selection && !selection.isCollapsed);
    };

    document.addEventListener('selectionchange', handleSelectionChange);
    return () => document.removeEventListener('selectionchange', handleSelectionChange);
  }, [isFocused]);

  // Get variant-specific styles
  const getVariantStyles = () => {
    switch (block.style.variant) {
      case 'heading1':
        return 'text-3xl font-bold';
      case 'heading2':
        return 'text-2xl font-bold';
      case 'heading3':
        return 'text-xl font-semibold';
      case 'quote':
        return 'text-lg italic border-l-4 border-primary/30 pl-4 text-foreground-secondary';
      default:
        return 'text-base';
    }
  };

  const getPlaceholder = () => {
    switch (block.style.variant) {
      case 'heading1':
        return 'Заголовок';
      case 'heading2':
        return 'Подзаголовок';
      case 'heading3':
        return 'Маленький заголовок';
      case 'quote':
        return 'Цитата...';
      default:
        return 'Напишите что-нибудь или нажмите / для добавления блока...';
    }
  };

  return (
    <div className="relative group">
      <FormatToolbar
        visible={showToolbar && isFocused}
        onFormat={handleFormat}
        onVariantChange={handleVariantChange}
        currentVariant={block.style.variant}
      />

      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onFocus={() => setIsFocused(true)}
        onBlur={() => {
          setIsFocused(false);
          setShowToolbar(false);
          // Final sync on blur
          if (editorRef.current) {
            const html = editorRef.current.innerHTML;
            const plainText = editorRef.current.innerText;
            lastContentRef.current = html;
            updateBlock(block.id, {
              content: { html, plainText },
              updatedAt: Date.now(),
            });
          }
        }}
        data-placeholder={getPlaceholder()}
        className={cn(
          'min-h-[1.5em] outline-none rounded-lg p-4',
          'transition-colors duration-200',
          getVariantStyles(),
          'empty:before:content-[attr(data-placeholder)] empty:before:text-foreground-tertiary empty:before:pointer-events-none',
          isFocused || isFocusedBlock
            ? 'bg-muted/30'
            : 'bg-transparent hover:bg-muted/20',
          // Lists styling
          '[&_ul]:list-disc [&_ul]:ml-6 [&_ul]:my-2',
          '[&_ol]:list-decimal [&_ol]:ml-6 [&_ol]:my-2',
          '[&_li]:my-1'
        )}
      />
    </div>
  );
}
